import { AdminField } from 'components/shared/dropdowns'
import {
  BooleanRadioButtonField,
  Box,
  FormModal,
  SpinnerButton,
  TextField,
  ValueAutocompleteField,
} from '@forgeglobal/design-framework'
import { DEFAULT_FEE } from 'util/PricingUtils'
import { Field, Form, FormRenderProps } from 'react-final-form'
import { MutationResult } from 'react-apollo'
import { PercentField } from 'components/shared/PercentField'
import { assetTypeOptions, dealBlockerOpts, opportunityCertainty } from 'util/Enums'
import { formatPercent, parsePercent } from 'util/NumberUtils'
import { useOpportunityDetailsQuery, useUpdateOpportunityDetailsMutation } from 'graphql/bindings/AdminBindings'
import React from 'react'
import omit from 'lodash/omit'

interface TradeIntentForm {
  id: string
  asset_type?: string
  share_type_description?: string
  fee?: string
  has_time_constraints?: boolean
  time_constraint_notes?: string
  has_flexibility?: boolean
  flexibility_notes?: string
  additional_diligence_notes?: string
}

interface FormShape {
  id: string
  certainty?: string
  deal_blockers: string[]
  owner?: { label: string; value: number }
  additional_owners: { label: string; value: number }[]
  trade_intent: TradeIntentForm
  mutationResult: MutationResult
}

const DetailsForm: React.FC<FormRenderProps<FormShape>> = ({ handleSubmit, values }) => {
  const mutateInProgress = values.mutationResult.called
  return (
    <form onSubmit={handleSubmit}>
      <ValueAutocompleteField name="trade_intent.asset_type" label="Type of shares" options={assetTypeOptions} />
      <Field
        name="trade_intent.share_type_description"
        label="Description(series, etc)"
        component={TextField}
        fullWidth
      />
      <ValueAutocompleteField name="certainty" label="Certainty" options={opportunityCertainty} />
      <ValueAutocompleteField
        name="deal_blockers"
        label="Deal Blockers (if applicable)"
        options={dealBlockerOpts}
        multiple
      />
      <PercentField name="trade_intent.fee" label="Fee" />
      <Field name="trade_intent.has_time_constraints" component={BooleanRadioButtonField} label="Time constraints?" />
      {values.trade_intent.has_time_constraints && (
        <Field
          name="trade_intent.time_constraint_notes"
          label="Describe time constraints"
          component={TextField}
          multiline
          fullWidth
        />
      )}
      <Field name="trade_intent.has_flexibility" component={BooleanRadioButtonField} label="Flexibility?" />
      {values.trade_intent.has_flexibility && (
        <Field
          name="trade_intent.flexibility_notes"
          label="Describe flexibility"
          component={TextField}
          multiline
          fullWidth
        />
      )}
      <AdminField name="owner" label="Marketplace owner" />
      <AdminField name="additional_owners" label="Additional Forge contact(s)" multiple />
      <Field
        name="trade_intent.additional_diligence_notes"
        label="Additional diligence required"
        component={TextField}
        multiline
        fullWidth
      />
      <Box mt={2}>
        <SpinnerButton
          variant="contained"
          color="primary"
          type="submit"
          fullWidth
          loading={mutateInProgress}
          caption="Save"
        />
      </Box>
    </form>
  )
}

export const OpportunityDetailsModal: React.FC<{ hideModal: () => void; opportunityId: string }> = ({
  hideModal,
  opportunityId,
}) => {
  const { data } = useOpportunityDetailsQuery({ variables: { opportunityId: opportunityId } })
  const [mutate, mutateResult] = useUpdateOpportunityDetailsMutation({
    onCompleted: hideModal,
  })
  const opportunity = data?.opportunity
  const trade = opportunity?.trade_intent
  const [firstOwner, ...otherOwners] = opportunity?.owners ?? []
  const onSubmit = (values: FormShape) => {
    const ownerIds = [values.owner, ...values.additional_owners].filter(owner => !!owner).map(owner => owner!.value)
    const fee = values?.trade_intent?.fee !== undefined ? parsePercent(values.trade_intent.fee) : DEFAULT_FEE
    mutate({
      variables: {
        form: {
          ...(omit(values, ['owner', 'additional_owners', 'mutationResult']) as any),
          trade_intent: {
            ...values.trade_intent,
            fee: fee,
          },
          ownerIds: ownerIds,
        },
      },
    })
  }

  const initialValues: FormShape = {
    id: opportunityId,
    certainty: opportunity?.certainty ?? undefined,
    deal_blockers: opportunity?.deal_blockers ?? [],
    owner: firstOwner && { label: firstOwner.name, value: firstOwner.owner_id },
    additional_owners: otherOwners.map(owner => ({ label: owner.name, value: owner.owner_id })),
    trade_intent: {
      id: trade?.id ?? '',
      asset_type: trade?.asset_type ?? undefined,
      share_type_description: trade?.share_type_description ?? '',
      fee: formatPercent(trade?.fee ?? DEFAULT_FEE),
      has_time_constraints: trade?.has_time_constraints ?? false,
      time_constraint_notes: trade?.time_constraint_notes ?? '',
      has_flexibility: trade?.has_flexibility ?? false,
      flexibility_notes: trade?.flexibility_notes ?? '',
      additional_diligence_notes: trade?.additional_diligence_notes ?? '',
    },
    mutationResult: mutateResult,
  }
  return (
    <FormModal open hideModal={hideModal} title="Edit: Opportunity Details">
      <Form<FormShape>
        component={DetailsForm}
        onSubmit={onSubmit}
        initialValues={{
          ...initialValues,
        }}
      />
    </FormModal>
  )
}
